import { doc, getDoc } from "firebase/firestore/lite";
import { useCallback, useEffect, useRef, useState } from "react";
import { db } from "../auth/firebase";
import { useGlobalState } from "../globalState/GlobalState";
import i18n from "./i18n";

export const useGetFireStoreSizes = (
  url?: string,
  placeholderSize = 128,
  sizes = [32, 64, 128, 320, 640, 768, 1024, 1280, 1536, 1920, 2560],
  subfolder = "resized",
) => {
  const [error, setError] = useState(false);
  if (!url)
    return { placeholderSrc: "", src: "", srcSet: "", sizes: "", error: true };
  const fileName =
    decodeURIComponent(new URL(url).pathname).split("/").pop() || "";
  const sources =
    sizes?.map((size) => ({
      size: size / 2,
      url: url?.replace(fileName, `${subfolder}%2F${fileName}_${size}x${size}`),
    })) || [];

  const loadedImage = new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject();
    image.src = sources[0]?.url;
  });
  loadedImage.catch(() => setError(true));
  const srcSet = sources.map(({ url, size }) => `${url} ${size}w`).join(", ");

  return {
    placeholderSrc:
      sources.find((img) => img.size === placeholderSize)?.url || url,
    src: url,
    srcSet: error ? undefined : srcSet,
    error,
  };
};

export const useGetTranslations = (project) => {
  const [_, setTranslation] = useGlobalState().translationsState;

  const fetchTranslations = async (lang) => {
    console.log("changing to", i18n.resolvedLanguage);
    const translationJsonPath = `organizations/${project?.organizationId}/projects/${project?.projectId}/translations/${lang}`;
    const translationJsonDoc = await getDoc(doc(db, translationJsonPath));
    if (!translationJsonDoc.exists()) return;
    const translationJson = translationJsonDoc.data();

    const { translations, originalToHashedMap } = translationJson;
    const unhashedTranslations = {};

    // List of keys to filter out when language ends with -simple
    // const keysToFilter = [
    //   project?.title,
    //   // Add all titles from project.content
    //   ...(project?.content?.map(item => item.title) || [])
    // ].filter(Boolean);

    for (const [hashedKey, value] of Object.entries(translations)) {
      const originalKey = originalToHashedMap?.[hashedKey] || hashedKey;
      // Skip if language ends with -simple and key is in the filter list
      // if (lang.endsWith("-simple") && keysToFilter.includes(originalKey)) {
      //   continue;
      // }
      unhashedTranslations[originalKey] = value;
    }
    console.log(translationJson);
    console.log(unhashedTranslations);
    setTranslation(unhashedTranslations);
  };

  return { fetchTranslations };
};

export const useDimensions = () => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const observeTarget = containerRef.current;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    if (observeTarget) {
      resizeObserver.observe(observeTarget);
    }

    return () => {
      if (observeTarget) {
        resizeObserver.unobserve(observeTarget);
      }
    };
  }, []);
  return [containerRef, dimensions] as const;
};
